import { Injectable } from '@angular/core';
import {
  HttpEvent, HttpInterceptor, HttpHandler, HttpRequest,
  HttpResponse, HttpErrorResponse, HttpClient, HttpHeaders,
  HttpContext, HttpContextToken
} from '@angular/common/http';

import { tap, catchError, switchMap, shareReplay, finalize, timeout } from 'rxjs/operators';
import { throwError, Observable, BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { LoginService } from 'src/app/services/login.service';
import { LoaderService } from 'src/app/services/loader.service';
import { EcryptServiceService } from '../auth/EcryptionService/ecrypt-service.service';
import { Constant } from 'src/app/constant/constant';
import { MatDialog } from '@angular/material/dialog';
import { environment } from 'src/environments/environment';

export const TOKEN = 'token';
export const AUTHENTICATED_USER = 'authenticaterUser';
export const USER_ID = 'ID';

/** Marks a request as already-retried after a 401, preventing infinite refresh loops. */
export const AUTH_RETRY_ATTEMPTED = new HttpContextToken<boolean>(() => false);

@Injectable()
export class CalsoftInterceptor implements HttpInterceptor {

  private static isRedirectingToSignOut = false;

  // Legacy lock — kept for backward compat; getSharedRefreshRequest() is now preferred
  private static isRefreshing = false;
  private static refreshTokenSubject = new BehaviorSubject<string | null>(null);

  // Single-flight refresh observable: all concurrent callers share one HTTP call
  private static refreshRequest$: Observable<any> | null = null;

  userLoggedIn: boolean;

  constructor(
    private basicAuthenticationService: LoginService,
    private router: Router,
    private loaderService: LoaderService,
    private ecryptServiceService: EcryptServiceService,
    private http: HttpClient,
    private dialog: MatDialog
  ) { }

  public onlineFlag = navigator.onLine;

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const basicAuthHeaderString = this.basicAuthenticationService.getStorageToken(Constant.X_XSRF_TOKEN);

    if (basicAuthHeaderString) {
      this.userLoggedIn = true;
      request = this.addToken(request);
    } else {
      this.userLoggedIn = false;
      
      const enterPriseCooke = this.basicAuthenticationService.getStorageToken(Constant.enterPriseKey);
      let headersConfig: any = {
        [Constant.DEVICE]: Constant.DEVICE_TYPE,
        [Constant.Source_Header]: Constant.source,
      };
      
      if (enterPriseCooke) {
        headersConfig[Constant.enterPriseKey] = enterPriseCooke;
      }
      
      request = request.clone({
        withCredentials: true,
        setHeaders: headersConfig,
      });
    }

    // Do NOT intercept the refresh-token call itself — would cause infinite loop
    const isRefreshTokenApi = request.url.includes('/api/auth/refresh/token') ||
      request.url.includes('/refresh/token');

    // ── Proactive refresh: renew before the token expires, not after a 401 ──────
    // Uses stored expiresAt metadata — no JWT decryption needed in Angular.
    // Only runs for authenticated, non-refresh calls.
    if (this.userLoggedIn && !isRefreshTokenApi && this.shouldProactivelyRefresh()) {
      return this.getSharedRefreshRequest().pipe(
        switchMap(() => next.handle(this.addToken(request))),
        // On proactive-refresh failure, still attempt the request — backend will
        // return 401 if truly expired, and the 401 handler will try again.
        catchError(() => next.handle(this.addToken(request)))
      );
    }

    return next.handle(request).pipe(
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          // Transparent backend renewal: store new encrypted token + expiry
          const newToken = event.headers.get('X-New-Access-Token');
          const expiresAt = event.headers.get('X-Access-Token-Expires-At');
          if (newToken) {
            this.storeToken(Constant.XSRF_TOKEN, newToken);
          }
          if (expiresAt && Number.isFinite(Number(expiresAt))) {
            this.basicAuthenticationService.setStoredAccessTokenExpiry(Number(expiresAt));
          }
        }
      }),
      catchError((err: any) => {
        if (err instanceof HttpErrorResponse && err.status === 401 && !isRefreshTokenApi) {
          if (request.context.get(AUTH_RETRY_ATTEMPTED)) {
            // Refresh was already tried and the retry still got 401 — give up and sign out.
            this.redirectOnAuthFailure(false);
            return throwError(err);
          }
          return this.handle401Error(request, next, err);
        }
        if (err instanceof HttpErrorResponse) {
          this.handleError(err);
        }
        return throwError(err);
      })
    );
  }

  /**
   * On 401:
   *  1. Try to get a new access token using the refresh token (still valid — infinite TTL).
   *  2. Retry the original request with the new access token.
   *  3. If refresh also fails → sign out.
   *
   *  Uses a lock (isRefreshing) so concurrent 401s queue behind a single refresh call.
   */
  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
    originalErr: HttpErrorResponse
  ): Observable<HttpEvent<any>> {

    // Check for concurrent session eviction — prefer structured code, fall back to free-text
    let isConcurrent = false;
    let errorMsg = '';
    try {
      // Structured error code (preferred — set by backend structured error response)
      const errorCode = originalErr.error?.code || originalErr.error?.error_code?.toString();
      if (errorCode === 'SESSION_REPLACED_BY_ANOTHER_LOGIN' || errorCode === 'DEVICE_SESSION_MISMATCH') {
        isConcurrent = true;
      }
      if (typeof originalErr.error === 'string') {
        errorMsg = originalErr.error;
      } else if (originalErr.error instanceof ArrayBuffer || originalErr.error instanceof Blob) {
        errorMsg = originalErr.headers?.get('x-error-message') || '';
      } else {
        errorMsg = originalErr.error?.error_message || originalErr.error?.message ||
          originalErr.error?.detail || JSON.stringify(originalErr.error || '');
      }
      if (!isConcurrent) {
        // Legacy free-text fallback — remove once backend returns structured codes
        const lower = errorMsg.toLowerCase();
        if (lower.includes('device') || lower.includes('another device') ||
          lower.includes('concurrent') || lower.includes('logged in from another') ||
          lower.includes('different device')) {
          isConcurrent = true;
          localStorage.setItem('concurrent_evicted', 'true');
        }
      }
    } catch (e) {
      // ignore parse errors
    }

    if (isConcurrent) {
      localStorage.setItem('concurrent_evicted', 'true');
      this.redirectOnAuthFailure(true);
      return throwError(originalErr);
    }

    // "Invalid Authorization header" means the token is malformed or missing entirely.
    // A refresh call would also fail — clear all storage and go straight to sign-in.
    const isInvalidHeader = errorMsg.toLowerCase().includes('invalid authorization');
    const hasNoToken = !this.basicAuthenticationService.getStorageToken(Constant.X_XSRF_TOKEN);
    if (isInvalidHeader || (hasNoToken && Constant.DEVICE_TYPE === 'MOBILE')) {
      localStorage.clear();
      sessionStorage.clear();
      this.redirectOnAuthFailure(false);
      return throwError(originalErr);
    }

    // --- Token Refresh Logic ---
    // Use the shared single-flight observable so concurrent 401s trigger only one
    // actual HTTP refresh call; all other callers receive the same result.
    return this.getSharedRefreshRequest().pipe(
      switchMap(() => {
        // Retry with AUTH_RETRY_ATTEMPTED=true so a second 401 is not re-refreshed
        const retried = request.clone({
          context: request.context.set(AUTH_RETRY_ATTEMPTED, true)
        });
        return next.handle(this.addToken(retried));
      }),
      catchError((refreshErr) => {
        this.redirectOnAuthFailure(false);
        return throwError(refreshErr);
      })
    );
  }

  private handleError(err: any): void {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 500) {
        // Optionally navigate to 500 page
        // this.router.navigate(['/500']);
      } else if (err.status === 0) {
        // status=0 = network failure (backend down, CORS, ERR_CONNECTION_REFUSED).
        // Do NOT treat this as an authentication failure — session state is intact.
        if (!navigator.onLine) {
          this.router.navigate(['/nointernet']);
        }
        // When online but unreachable (service down / CORS): do nothing.
        // The caller will receive the error; session cookies/localStorage are preserved.
      }
    }
  }

  /** Full sign-out clear: matches sign-out.component.ts logic exactly, then hard-redirects. */
  private redirectOnAuthFailure(isConcurrent: boolean): void {
    if (CalsoftInterceptor.isRedirectingToSignOut) {
      return;
    }

    const currentPath = window.location.pathname;
    const alreadyOnConcurrent = currentPath.includes('unauthorized');
    const alreadyOnSignIn = currentPath.includes('sign-in');
    const alreadyOnSignOut = currentPath.includes('sign-out');

    if (alreadyOnSignIn || alreadyOnSignOut || alreadyOnConcurrent) {
      return;
    }

    CalsoftInterceptor.isRedirectingToSignOut = true;

    // --- Full storage + cache clear ---
    this.basicAuthenticationService.clearSessionData();
    sessionStorage.clear();
    localStorage.clear();

    localStorage.removeItem('trialExpiredMessage');
    localStorage.removeItem('trialExpiryData');

    // Clear auth cookies
    document.cookie = 'x-xsrf-token=;expires=' + new Date(0).toUTCString() + ';path=/';
    document.cookie = 'cxi=;expires=' + new Date(0).toUTCString() + ';path=/';
    document.cookie = 'cci=;expires=' + new Date(0).toUTCString() + ';path=/';
    document.cookie = 'user-name=;expires=' + new Date(0).toUTCString() + ';path=/';
    document.cookie = 'user-id=;expires=' + new Date(0).toUTCString() + ';path=/';

    this.dialog.closeAll();

    // Hard redirect — works even before Angular router is fully active (e.g. during guard phase)
    const targetPath = isConcurrent ? '/unauthorized?reason=concurrent' : '/sign-in';
    setTimeout(() => {
      window.location.href = targetPath;
    }, 100);
  }

  /**
   * Store a token value correctly for both cookie-based (web) and localStorage (mobile) sessions.
   */
  private storeToken(key: string, value: string): void {
    if (Constant.DEVICE_TYPE === 'MOBILE') {
      localStorage.setItem(key, value);
    } else {
      // Web uses cookies — SameSite=Strict prevents CSRF; Secure ensures HTTPS-only
      const secureFlag = location.protocol === 'https:' ? ';Secure' : '';
      document.cookie = `${key}=${value};path=/;SameSite=Strict${secureFlag}`;
    }
  }

  /**
   * Returns true if the stored access-token expiry is within the 4-minute refresh window.
   * Uses locally-stored expiresAt metadata — never decrypts the JWT in Angular.
   */
  private shouldProactivelyRefresh(): boolean {
    const expiresAt = this.basicAuthenticationService.getStoredAccessTokenExpiry();
    if (!expiresAt) return false;
    const windowMs = (Constant.authProactiveRefreshWindowMs || 3 * 60 * 1000)
                   + (Constant.authClockSkewMs || 60 * 1000);
    return (expiresAt - Date.now()) <= windowMs;
  }

  private getSharedRefreshRequest(): Observable<any> {
    if (!CalsoftInterceptor.refreshRequest$) {
      const timeoutMs = Constant.authRefreshTimeoutMs || 10_000;
      CalsoftInterceptor.refreshRequest$ = this.basicAuthenticationService
        .getNewAccessToken()
        .pipe(
          timeout(timeoutMs),
          tap((res: any) => {
            const token = res?.data?.accessToken || res?.accessToken;
            if (token && typeof token === 'string') {
              this.storeToken(Constant.XSRF_TOKEN, token);
            }
            const expiresAt = res?.data?.expiresAt || res?.expiresAt;
            if (expiresAt && Number.isFinite(Number(expiresAt))) {
              this.basicAuthenticationService.setStoredAccessTokenExpiry(Number(expiresAt));
            }

            // AST-102 = "Token Cannot be renewed!" — server returned HTTP 200 but
            // the refresh itself failed. Treat this as a hard auth failure so the
            // catchError in handle401Error fires and redirects to /sign-in.
            const statusCode = res?.status_code || res?.data?.status_code;
            const hasValidToken = token && typeof token === 'string';
            if (!hasValidToken || statusCode === 'AST-102') {
              throw new Error('TOKEN_REFRESH_FAILED: ' + (res?.status_message || statusCode || 'no token returned'));
            }
          }),
          shareReplay({ bufferSize: 1, refCount: false }),
          finalize(() => {
            CalsoftInterceptor.refreshRequest$ = null;
          })
        );
    }
    return CalsoftInterceptor.refreshRequest$;
  }

  addToken(request: HttpRequest<any>) {
    const enterPriseCooke = this.basicAuthenticationService.getStorageToken(Constant.enterPriseKey);
    const basicAuthHeaderString = this.basicAuthenticationService.getStorageToken(Constant.X_XSRF_TOKEN);

    if (basicAuthHeaderString !== '') {
      const headersConfig: any = {
        [Constant.X_XSRF_TOKEN]: this.basicAuthenticationService.getStorageToken(Constant.X_XSRF_TOKEN),
        [Constant.XSRF_TOKEN]: this.basicAuthenticationService.getStorageToken(Constant.XSRF_TOKEN),
        [Constant.DEVICE]: Constant.DEVICE_TYPE,
        [Constant.Source_Header]: Constant.source,
      };

      if (enterPriseCooke) {
        headersConfig[Constant.enterPriseKey] = enterPriseCooke;
      }

      return request.clone({ setHeaders: headersConfig });
    } else {
      return request;
    }
  }
}
